import React, { useState, useEffect } from "react";
import { Button } from "@material-ui/core";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import ButtonTextLoading from "../Shared/ButtonTextLoading";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";

import { useFormik } from "formik";
import * as Yup from "yup";
import { resetPasswordAdmin } from "../../../service/endpoint-manager";

import { useSnackbar } from "notistack";

import styles from "../../../styles.module.css"; // Importing CSS module for styles.
import clsx from "clsx";

const useStyles = makeStyles((theme) => ({
  paper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: "#3f51b5",
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(3),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },

  dateContainer: {
    display: "flex",
    "&>*": {
      flexBasis: "20%",
      flex: "1",
    },
  },

  yearContainer: {
    margin: "0 0 0 0.5rem",
    paddingTop: "0.2rem",
    paddingBottom: "0.2rem",
    display: "flex",
    justifyContent: "center",
    [theme.breakpoints.down("md")]: {
      margin: "0",
      padding: "0.5rem",
    },
  },
  monthContainer: {
    margin: "0 5px 0 0",
    paddingTop: "0.2rem",
    paddingBottom: "0.2rem",
    display: "flex",
    justifyContent: "center",
    [theme.breakpoints.down("md")]: {
      margin: "0",
      padding: "0.5rem",
    },
  },
  dayContainer: {
    margin: "0 5px",
    paddingTop: "0.2rem",
    paddingBottom: "0.2rem",
    display: "flex",
    justifyContent: "center",
    [theme.breakpoints.down("md")]: {
      margin: "0",
      padding: "0.5rem",
    },
  },

  year: {
    width: "100%",
    outline: "none",
    borderRadius: "8%",
    padding: "0.7rem",
    paddingRight: "0",

    [theme.breakpoints.down("md")]: {
      maxHeight: "70vh",
      overflow: "scroll",
    },
  },

  month: {
    width: "100%",
    outline: "none",
    borderRadius: "8%",
    padding: " 0.7rem",
    paddingRight: "0",
    [theme.breakpoints.down("md")]: {
      maxHeight: "70vh",
      overflow: "scroll",
    },
  },

  day: {
    width: "100%",
    outline: "none",
    borderRadius: "8%",
    padding: "0.7rem",
    paddingRight: "0",
    [theme.breakpoints.down("md")]: {
      maxHeight: "70vh",
      overflow: "hidden",
    },
  },

  btnStyles: {
    background: "#1B2132",
    color: "#fff",
    fontSize: ".8rem",
  },

  formControl: {
    width: "100%",
  },

  menuItems: { display: "block" },

  dropdown: {
    borderTopRightRadius: "0",
    borderBottomRightRadius: "0",
    marginRight: "5%",
    backgroundColor: "#f7f7f7",
    width: "100%",
  },
}));

export default function ResetPasswordAdmin({
  handleCloseResetPasswordDialog,
  userName,
}) {
  const classes = useStyles();
  const [passwordsMatch, setPasswordsMatch] = useState(true);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const formik = useFormik({
    initialValues: {
      userName: userName,
      password: "",
      confirmPassword: "",
    },
    onSubmit: async function (values) {
      setIsSubmitting(true);

      const result = await resetPasswordAdmin(values);

      if (result.status === 200) {
        enqueueSnackbar(`${result?.data?.message}`, {
          variant: "success",
        });
        handleCloseResetPasswordDialog();
      } else {
        enqueueSnackbar(`${result?.response?.data?.message}`, {
          variant: "error",
        });
      }

      setIsSubmitting(false);
    },

    // validation schema for formik using yup
    validationSchema: Yup.object({
      // username must be 6-20 and is required
      userName: Yup.string()
        .min(6, "At least 6 characters required")
        .max(20)
        .required("Username is required"),

      // password must be at least 6 characters and is required
      password: Yup.string()
        .min(8, "At least 8 characters required")
        .required("Password is required"),

      // password confirmation must be at least 6 characters and is required
      confirmPassword: Yup.string()
        .min(8, "At least 8 characters required")
        .required("Confirm Password is required"),
    }),
  });

  // this useEffect checks to make sure both password and confirmPassword values are supplied
  //and match - sets state accordingly
  useEffect(() => {
    if (
      formik.values.password.length > 0 &&
      formik.values.confirmPassword.length > 0 &&
      formik.values.password !== formik.values.confirmPassword
    ) {
      setPasswordsMatch(false);
    } else {
      setPasswordsMatch(true);
    }
  }, [formik.values.password, formik.values.confirmPassword]);

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <>
        <div className={styles.paper}>
          <Typography component="h1" variant="h6" className={styles.heading}>
            Reset Password
          </Typography>
          <form
            onSubmit={formik.handleSubmit}
            className={classes.form}
            noValidate
          >
            <Grid container spacing={2}>
              {/* username */}
              <Grid item xs={12}>
                <TextField
                  error={formik.touched.userName && formik.errors.userName}
                  required
                  fullWidth
                  id="userName"
                  label="Username"
                  name="userName"
                  autoComplete="userName"
                  variant="outlined"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.userName}
                  disabled={true}
                />
                {
                  <div
                    style={{
                      color: "red",
                      fontSize: "0.6rem",
                      visibility:
                        formik.touched.userName && formik.errors.userName
                          ? "visible"
                          : "hidden",
                    }}
                  >
                    {formik.errors.userName || "Username required"}
                  </div>
                }
              </Grid>

              {/* password */}
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  name="password"
                  label="New Password"
                  type="password"
                  id="password"
                  autoComplete="new-password"
                  variant="outlined"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.password}
                />
                {
                  <div
                    style={{
                      color: "red",
                      fontSize: "0.6rem",
                      visibility:
                        formik.touched.password && formik.errors.password
                          ? "visible"
                          : "hidden",
                    }}
                  >
                    {formik.errors.password || "Password required"}
                  </div>
                }
              </Grid>

              {/* password confirmation */}
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  name="confirmPassword"
                  label="Confirm New Password"
                  type="password"
                  id="confirmPassword"
                  autoComplete="confirm-password"
                  variant="outlined"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.confirmPassword}
                />
                {
                  <div style={{ display: "flex" }}>
                    <div
                      style={{
                        color: "red",
                        fontSize: "0.6rem",
                        visibility:
                          formik.touched.confirmPassword &&
                          formik.errors.confirmPassword
                            ? "visible"
                            : "hidden",
                      }}
                    >
                      {formik.errors.confirmPassword}
                    </div>
                    {/* will be shown if passwords dont match as an error message */}
                    {
                      <div
                        style={{
                          color: "red",
                          fontSize: "0.6rem",
                          visibility: !passwordsMatch ? "visible" : "hidden",
                        }}
                      >
                        {formik.errors.confirmPassword
                          ? `, Passwords don't match`
                          : `Passwords don't match`}
                      </div>
                    }
                  </div>
                }
              </Grid>
            </Grid>

            {/* submit button */}
            <Button
              type="submit"
              // button will be disabled if form has errors or passwords dont match
              disabled={
                Object.keys(formik.errors).length > 0 || !passwordsMatch
              }
              fullWidth
              variant="contained"
              color="primary"
              className={clsx(
                `${styles.primaryButton} ${styles.dialogResetButton}`
              )}
            >
              <ButtonTextLoading
                isSubmitting={isSubmitting}
                body={"Reset Password"}
              />
            </Button>
          </form>
        </div>
      </>
    </Container>
  );
}
